// apps/client/src/lib/appCheckRecovery.ts
//
// Firebase App Check rejection classification, IndexedDB throttle-state cleanup,
// and PII-safe email hashing for telemetry.
//
// Background:
// - App Check is enforced on identitytoolkit/firestore/storage in valutten-prod.
// - When a reCAPTCHA Enterprise verdict scores < 0.5 the Firebase JS SDK throttles
//   the client for 24h on first 403, leaving the user with no actionable error.
// - This module classifies those errors and provides a recovery path that wipes
//   the SDK's IndexedDB throttle state so the user can re-attempt immediately.

export interface AppCheckErrorContext {
  isAppCheckFailure: boolean;
  throttleRemainingMs: number | null;
  isInitialThrottle: boolean;
  raw: unknown;
}

interface FirebaseLikeError {
  code?: unknown;
  message?: unknown;
}

const FIREBASE_APP_CHECK_DB_PREFIX = 'firebase-app-check';

const APP_CHECK_MESSAGE_PATTERN = /AppCheck:\s*(Requests throttled|403 error)/i;
const APP_CHECK_GENERIC_MESSAGE_HINT = /app[-\s]check/i;
const THROTTLE_PATTERN_DAYS =
  /Attempts allowed again after\s+(\d+)d:(\d+)m:(\d+)s/i;
const THROTTLE_PATTERN_HOURS = /(\d+)h:(\d+)m:(\d+)s/i;
const INITIAL_THROTTLE_HINT = /appCheck\/initial-throttle/i;

const isFirebaseLikeError = (err: unknown): err is FirebaseLikeError =>
  typeof err === 'object' && err !== null;

const readString = (value: unknown): string => (typeof value === 'string' ? value : '');

const parseThrottleRemainingMs = (message: string): number | null => {
  const daysMatch = message.match(THROTTLE_PATTERN_DAYS);
  if (daysMatch) {
    const days = Number(daysMatch[1]);
    const minutes = Number(daysMatch[2]);
    const seconds = Number(daysMatch[3]);
    if ([days, minutes, seconds].every((n) => Number.isFinite(n))) {
      return ((days * 24 * 60 + minutes) * 60 + seconds) * 1000;
    }
  }
  const hoursMatch = message.match(THROTTLE_PATTERN_HOURS);
  if (hoursMatch) {
    const hours = Number(hoursMatch[1]);
    const minutes = Number(hoursMatch[2]);
    const seconds = Number(hoursMatch[3]);
    if ([hours, minutes, seconds].every((n) => Number.isFinite(n))) {
      return ((hours * 60 + minutes) * 60 + seconds) * 1000;
    }
  }
  return null;
};

export function classifyAuthError(err: unknown): AppCheckErrorContext {
  const ctx: AppCheckErrorContext = {
    isAppCheckFailure: false,
    throttleRemainingMs: null,
    isInitialThrottle: false,
    raw: err,
  };

  if (!isFirebaseLikeError(err)) {
    return ctx;
  }

  const code = readString(err.code);
  const message = readString(err.message);

  const codeIsAppCheckInvalid = code === 'auth/firebase-app-check-token-is-invalid';
  const codeIsInternalWithAppCheckMessage =
    code === 'auth/internal-error' && APP_CHECK_GENERIC_MESSAGE_HINT.test(message);
  const messageMatchesAppCheck = APP_CHECK_MESSAGE_PATTERN.test(message);

  ctx.isAppCheckFailure =
    codeIsAppCheckInvalid || codeIsInternalWithAppCheckMessage || messageMatchesAppCheck;

  if (!ctx.isAppCheckFailure) {
    return ctx;
  }

  ctx.throttleRemainingMs = parseThrottleRemainingMs(message);
  ctx.isInitialThrottle = INITIAL_THROTTLE_HINT.test(message);

  return ctx;
}

/**
 * Delete all IndexedDB databases whose name starts with `firebase-app-check`.
 * This clears the Firebase JS SDK's local throttle state so the user can
 * retry immediately rather than wait out the 24h backoff.
 *
 * `indexedDB.databases()` is unavailable in older Safari and Firefox — we
 * feature-detect and resolve immediately in that case (the user falls back
 * to the "open in incognito" path).
 */
export async function clearAppCheckThrottleState(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;

  const idbWithDatabases = indexedDB as IDBFactory & {
    databases?: () => Promise<Array<{ name?: string }>>;
  };

  if (typeof idbWithDatabases.databases !== 'function') {
    return;
  }

  let dbs: Array<{ name?: string }> = [];
  try {
    dbs = await idbWithDatabases.databases();
  } catch {
    return;
  }

  const targets = dbs
    .map((d) => d.name)
    .filter((name): name is string =>
      typeof name === 'string' && name.startsWith(FIREBASE_APP_CHECK_DB_PREFIX)
    );

  await Promise.all(
    targets.map(
      (name) =>
        new Promise<void>((resolve) => {
          try {
            const req = indexedDB.deleteDatabase(name);
            req.onsuccess = () => resolve();
            req.onerror = () => resolve();
            req.onblocked = () => resolve();
          } catch {
            resolve();
          }
        })
    )
  );
}

/**
 * SHA-256 hash of a normalized email address, returning the first 16 hex chars.
 * Normalization: trim + lowercase. Used so we can correlate auth failures by
 * actor in PostHog without storing raw PII.
 */
export async function hashEmail(email: string): Promise<string> {
  const normalized = email.trim().toLowerCase();
  const bytes = new TextEncoder().encode(normalized);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const hex = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hex.slice(0, 16);
}
